import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { PAGE_TITLES } from "~/configs/pageTitles";
import { useLinkDetailPageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import { TaskAttributes, Task as TaskType } from "../../../api/Api";
import { apiClient } from "../../../api/ApiClient";
import Button from "../../../components/ui/button/Button";
import Card from "../../../components/ui/card/Card";
import styles from "./OutreachLinkDetailPage.module.scss";

// Reusing same styles

// Define the response type that matches the actual API response
interface TaskResponse {
  task: TaskType;
}

// Status badge component
const StatusBadge = ({ status }: { status: string }) => {
  const getStatusClass = () => {
    switch (status) {
      case "DONE":
        return styles.statusDone;
      case "DOING":
        return styles.statusDoing;
      case "CANCELLED":
        return styles.statusCancelled;
      case "TODO":
      default:
        return styles.statusTodo;
    }
  };

  return (
    <span className={`${styles.statusBadge} ${getStatusClass()}`}>
      {status}
    </span>
  );
};

const BrandLinkDetailPage = () => {
  const { id: siteId, taskId } = useParams();

  if (!siteId) {
    throw Error("No site id provided");
  }

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [notes, setNotes] = useState("");
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [focusKeyword, setFocusKeyword] = useState<string | null>(null);
  const [notesInitialized, setNotesInitialized] = useState(false);

  // Fetch site data to get focus keyword
  const { data: siteData } = useQuery({
    queryKey: ["siteFocusKeyword", siteId],
    queryFn: async () => {
      const numericSiteId = Number(siteId);

      if (isNaN(numericSiteId)) {
        throw new Error("Invalid site ID");
      }

      const { data } = await apiClient.sites.sitesDetail(numericSiteId);

      return { focus_keyword: data?.site?.focus_keyword_name || null };
    },
    enabled: !!siteId,
  });

  // Fetch brand link data
  const {
    data: taskResponse,
    isLoading,
    error,
  } = useQuery<TaskResponse>({
    queryKey: ["brandTask", siteId, taskId],
    queryFn: async () => {
      try {
        // Convert IDs to numbers and validate
        const numericSiteId = Number(siteId);
        const numericTaskId = Number(taskId);

        if (isNaN(numericSiteId) || isNaN(numericTaskId)) {
          throw new Error("Invalid site ID or task ID");
        }

        // Use the API endpoint to get a single task
        const response = await apiClient.sites.tasksDetail2(
          numericSiteId,
          numericTaskId,
        );

        if (!response.data) {
          throw new Error("No data returned from API");
        }

        // The API returns the data in a format we don't expect, so we have to cast it
        return response.data as unknown as TaskResponse;
      } catch (error) {
        console.error("Error fetching task:", error);
        throw error;
      }
    },
    enabled: !!siteId && !!taskId,
    retry: 2, // Retry the query up to 2 times if it fails
    retryDelay: 1000, // Wait 1 second between retries
  });

  // Extract the actual task data from the response
  const task = taskResponse?.task;

  // Set the page title with the enhanced pattern
  useLinkDetailPageTitle(task?.link_domain, PAGE_TITLES.foundationLinks);

  // Initialize notes and linkUrl only on first load — never overwrite user edits on refetch
  useEffect(() => {
    if (!task) return;

    if (!notesInitialized) {
      setNotes(task.notes ?? "");
      setNotesInitialized(true);
    }

    if (task.built_link_url && !linkUrl) {
      setLinkUrl(task.built_link_url);
    }

    // Focus keyword is read-only display; safe to sync on every refetch.
    if (task.focus_keyword) {
      setFocusKeyword(task.focus_keyword);
    } else if (siteData?.focus_keyword) {
      setFocusKeyword(siteData.focus_keyword);
    }
  }, [task, siteData, notesInitialized, linkUrl]);

  const handleFinishBrandLink = async () => {
    if (!task || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const updateData: TaskAttributes = {
        status: "DONE",
      };

      // If link URL is provided, also save it
      if (linkUrl.trim()) {
        updateData.built_link_url = linkUrl.trim();
      }

      await apiClient.sites.tasksPartialUpdate(
        Number(siteId),
        Number(taskId),
        updateData,
      );

      // Update the local cache to reflect the changes without requiring a page refresh
      if (task) {
        // Create an updated task object
        const updatedTask = {
          ...task,
          status: "DONE",
          built_link_url: linkUrl.trim() || task?.built_link_url,
        };

        // Update the query cache
        queryClient.setQueryData(["brandTask", siteId, taskId], {
          task: updatedTask,
        });
      }

      // Navigate back to the brand links list
      navigate(routes.site.links.brand({ id: siteId }));
    } catch (error) {
      console.error("Error updating brand link:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!task || isCancelling) return;

    setIsCancelling(true);

    try {
      const updateData: TaskAttributes = {
        //@ts-expect-error @TODO: not clear why this is needed
        status: "CANCELLED",
      };

      await apiClient.sites.tasksPartialUpdate(
        Number(siteId),
        Number(taskId),
        updateData,
      );

      navigate(routes.site.links.brand({ id: siteId }));
    } catch (error) {
      console.error("Error cancelling brand link:", error);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!task || isSavingNotes) return;

    setIsSavingNotes(true);

    try {
      const updateData: TaskAttributes = {
        notes: notes,
      };

      await apiClient.sites.tasksPartialUpdate(
        Number(siteId),
        Number(taskId),
        updateData,
      );
    } catch (error) {
      console.error("Error saving notes:", error);
    } finally {
      setIsSavingNotes(false);
    }
  };

  // Format probability as percentage
  const formatProbability = (value: number | null) => {
    if (value === null) return "0%";

    return `${Math.round(value)}%`;
  };

  // Display loading state
  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ marginBottom: "20px" }}>
            Loading foundation link details...
          </div>
        </div>
      </div>
    );
  }

  // Display error state
  if (error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <div style={{ textAlign: "center", color: "red" }}>
          <div style={{ marginBottom: "20px", fontWeight: "bold" }}>
            Error loading foundation link details
          </div>
          <div>
            {error instanceof Error ? error.message : "Unknown error occurred"}
          </div>
          <Button
            style={{ marginTop: "20px" }}
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.contentLayout}>
        <div className={styles.mainContent}>
          <Card>
            <div className={styles.linkDetailsHeader}>
              <h1 className={styles.title}>
                {task?.link_domain && (
                  <div className={styles.faviconContainer}>
                    <img
                      src={`https://www.google.com/s2/favicons?domain=${task.link_domain}&sz=32`}
                      alt=""
                      className={styles.favicon}
                      onError={(e) => {
                        // Replace with generic icon if favicon fails to load
                        (e.target as HTMLImageElement).src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='32' height='32'%3E%3Cpath fill='%23ccc' d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z'/%3E%3C/svg%3E";
                      }}
                    />
                  </div>
                )}
                {task?.link_title || task?.link_domain}
              </h1>
            </div>

            <div className={styles.messageSection}>
              <p>
                Foundation opportunities help establish your brand across
                trusted web properties, directories, and profile pages. These
                placements strengthen your site's baseline credibility and
                support long-term search visibility.
              </p>

              <div className={styles.contributorSection}>
                <div className={styles.contributorInstructionsList}>
                  <div className={styles.contributorInstructionItem}>
                    <div className={styles.stepNumber}>1</div>
                    <div className={styles.stepContent}>
                      <div className={styles.stepTitle}>
                        Review the site requirements
                      </div>
                      <div className={styles.stepDescription}>
                        Visit the site and confirm whether it supports a
                        business profile, listing, or citation.
                      </div>
                    </div>
                  </div>

                  <div className={styles.contributorInstructionItem}>
                    <div className={styles.stepNumber}>2</div>
                    <div className={styles.stepContent}>
                      <div className={styles.stepTitle}>
                        Create your business profile
                      </div>
                      <div className={styles.stepDescription}>
                        Complete the registration or profile setup process using
                        your company's official business information.
                      </div>
                    </div>
                  </div>

                  <div className={styles.contributorInstructionItem}>
                    <div className={styles.stepNumber}>3</div>
                    <div className={styles.stepContent}>
                      <div className={styles.stepTitle}>
                        Locate the appropriate listing fields
                      </div>
                      <div className={styles.stepDescription}>
                        Some platforms may require you to navigate to a profile,
                        company page, or listing section before adding your
                        website details.
                      </div>
                    </div>
                  </div>

                  <div className={styles.contributorInstructionItem}>
                    <div className={styles.stepNumber}>4</div>
                    <div className={styles.stepContent}>
                      <div className={styles.stepTitle}>
                        Use consistent business information
                      </div>
                      <div className={styles.stepDescription}>
                        <p>
                          When available, include your business name, website,
                          and other core details exactly as they appear across
                          your primary web properties.
                        </p>
                        <p>
                          Where relevant, incorporate your target keyword
                          naturally in the description:
                          <span className={styles.keywordHighlight}>
                            {focusKeyword || "your focus keyword"}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles.emailTemplate}>
                  After publishing the listing, paste the live URL into the form
                  so we can track the placement.
                </div>
              </div>
            </div>
          </Card>

          <div className={styles.notesCard}>
            <h2>Notes</h2>
            <textarea
              className={styles.notesTextarea}
              placeholder="Place your notes here for the task"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <Button
              className={styles.notesButton}
              onClick={handleSaveNotes}
              disabled={isSavingNotes}
            >
              {isSavingNotes ? "Saving..." : "Save Notes"}
            </Button>
          </div>
        </div>

        <div className={styles.sideContent}>
          <Card>
            <div className={styles.detailsSection}>
              <h2>Details</h2>
              <div className={styles.detailsGrid}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>DOMAIN:</span>
                  <span className={styles.detailValue}>
                    {task?.link_domain ? (
                      <a
                        href={`https://${task.link_domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {task.link_domain}
                      </a>
                    ) : null}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>TYPE:</span>
                  <span className={styles.detailValue}>{task?.category}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>RELEVANCE:</span>
                  <span className={styles.detailValue}>
                    {task?.probability !== undefined
                      ? formatProbability(task.probability)
                      : null}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>TITLE:</span>
                  <span className={styles.detailValue}>
                    {task?.link_title || task?.link_domain || ""}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>SIGNUP:</span>
                  <span className={styles.detailValue}>
                    {task?.link_page_url ? (
                      <a
                        href={
                          task.link_page_url.startsWith("http")
                            ? task.link_page_url
                            : `https://${task.link_page_url}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {task.link_page_url}
                      </a>
                    ) : null}
                  </span>
                </div>
                {task?.status === "DONE" && task.built_link_url && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>COMPLETED LINK:</span>
                    <span className={styles.detailValue}>
                      <a
                        href={
                          task.built_link_url.startsWith("http")
                            ? task.built_link_url
                            : `https://${task.built_link_url}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {task.built_link_url}
                      </a>
                    </span>
                  </div>
                )}
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>FOCUS KEYWORD:</span>
                  <span className={styles.detailValue}>
                    {focusKeyword || ""}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>STATUS:</span>
                  <span className={styles.detailValue}>
                    <StatusBadge status={task?.status || "TODO"} />
                  </span>
                </div>
              </div>
            </div>

            <div style={{ marginTop: "var(--lg)", marginBottom: "var(--lg)" }}>
              <h3
                style={{
                  marginBottom: "var(--md)",
                  fontSize: "var(--text-sm)",
                  fontWeight: "400",
                }}
              >
                {(task?.status as string) === "DONE"
                  ? "UPDATE YOUR LINK"
                  : "SAVE YOUR LINK"}
              </h3>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--md)",
                }}
              >
                <input
                  type="text"
                  className={styles.notesTextarea}
                  style={{
                    padding: "var(--md)",
                    minHeight: "auto",
                    border: "2px solid var(--border-color, #e0e0e0)",
                    borderRadius: "var(--radius-md, 8px)",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    outline: "none",
                    transition: "border-color 0.2s ease",
                    backgroundColor: "var(--bg-white)",
                    color: "var(--text-black)",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor =
                      "var(--service-blue2, #90c6ff)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "var(--border-color, #e0e0e0)";
                  }}
                  placeholder="Link URL"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                />
              </div>
            </div>

            <div className={styles.actions}>
              <Button onClick={handleFinishBrandLink} disabled={isSubmitting}>
                {isSubmitting
                  ? "Saving..."
                  : (task?.status as string) === "DONE"
                    ? "Update Link"
                    : "Finish Foundation Link"}
              </Button>
              {(task?.status as string) !== "DONE" && (
                <Button outlined onClick={handleCancel} disabled={isCancelling}>
                  {isCancelling ? "Cancelling..." : "Cancel"}
                </Button>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BrandLinkDetailPage;
